import apiClient from '@/lib/api-client'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useWorkspaceStore } from '@/shared/stores/workspaceStore'

export interface UserSettings {
  id: string
  userId: string
  name?: string
  theme: string
  dateFormat: string
  timeFormat: string
  emailNotifications: boolean
  desktopNotifications: boolean
  soundEnabled: boolean
  createdAt: string
  updatedAt: string
}

export interface UpdateUserSettingsDto {
  name?: string
  theme?: string
  dateFormat?: string
  timeFormat?: string
  emailNotifications?: boolean
  desktopNotifications?: boolean
  soundEnabled?: boolean
}

export interface WorkspaceTimeSettings {
  id: string
  workspaceId: string
  roundingInterval: string
  approvalRequired: boolean
  autoStopTimerHours: number
  allowOverlaps: boolean
  createdAt: string
  updatedAt: string
}

export interface UpdateTimeTrackingSettingsDto {
  roundingInterval?: string
  approvalRequired?: boolean
  autoStopTimerHours?: number
  allowOverlaps?: boolean
}

export const settingsService = {
  async getUserSettings(): Promise<UserSettings> {
    const response = await apiClient.get('/users/me/settings')
    return response.data.data
  },

  async updateUserSettings(data: UpdateUserSettingsDto): Promise<UserSettings> {
    const response = await apiClient.patch('/users/me/settings', data)
    return response.data.data
  },

  async getWorkspaceSettings(workspaceId: string): Promise<WorkspaceTimeSettings> {
    const response = await apiClient.get(`/workspaces/${workspaceId}/settings`)
    return response.data.data
  },

  async updateWorkspaceSettings(workspaceId: string, data: UpdateTimeTrackingSettingsDto): Promise<WorkspaceTimeSettings> {
    const response = await apiClient.patch(`/workspaces/${workspaceId}/settings`, data)
    return response.data.data
  },
}

export function useUserSettings() {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => settingsService.getUserSettings(),
  })

  const mutation = useMutation({
    mutationFn: (data: UpdateUserSettingsDto) => settingsService.updateUserSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-settings'] })
    },
  })

  return {
    settings: query.data,
    isLoading: query.isLoading,
    updateSettings: mutation.mutate,
    isUpdating: mutation.isPending,
  }
}

export function useWorkspaceSettings() {
  const { selectedWorkspace } = useWorkspaceStore()
  const workspaceId = selectedWorkspace?.id
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['workspace-settings', workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error('No workspace selected')
      return settingsService.getWorkspaceSettings(workspaceId)
    },
    enabled: !!workspaceId,
  })

  const mutation = useMutation({
    mutationFn: (data: UpdateTimeTrackingSettingsDto) => {
      if (!workspaceId) throw new Error('No workspace selected')
      return settingsService.updateWorkspaceSettings(workspaceId, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-settings', workspaceId] })
    },
  })

  return {
    settings: query.data,
    isLoading: query.isLoading,
    updateSettings: mutation.mutate,
    isUpdating: mutation.isPending,
  }
}

export default settingsService
